import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, ArrowLeft, Clock, MapPin } from 'lucide-react';
import { trendingCountries, trendingStates } from '../mock';
import SafeImage from '../components/SafeImage';
import OfferRibbon from '../components/OfferRibbon';
import PriceTag from '../components/PriceTag';
import WhatsAppShareBtn from '../components/WhatsAppShareBtn';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePackages } from '../store';

const SearchResults = () => {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const q = (params.get('q') || '').trim().toLowerCase();
  const { packages: allPackages } = usePackages();

  const results = useMemo(() => {
    if (!q) return allPackages;
    return allPackages.filter((p) => {
      const hay = (p.title + ' ' + p.itinerary + ' ' + p.duration).toLowerCase();
      return hay.includes(q);
    });
  }, [q, allPackages]);

  const matchedDests = useMemo(() => {
    if (!q) return [];
    return [...trendingCountries, ...trendingStates].filter((d) => d.name.toLowerCase().includes(q));
  }, [q]);

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-[#0d64a8]">
        <Header />
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 pt-24 pb-10">
          <Link to="/" className="flex items-center gap-1.5 text-white/90 hover:text-white text-sm mb-4">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <div className="flex items-center gap-3 text-white">
            <Search size={22} />
            <h1 className="text-2xl md:text-3xl font-bold">
              {q ? <>Search results for “<span className="text-[#ffcc29]">{q}</span>”</> : 'All Packages'}
            </h1>
          </div>
          <p className="text-white/80 text-sm mt-1">{results.length} package{results.length === 1 ? '' : 's'} found</p>
        </div>
      </div>

      <section className="py-10">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          {matchedDests.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm text-gray-500 uppercase tracking-widest mb-3">Matching Destinations</h3>
              <div className="flex flex-wrap gap-2">
                {matchedDests.map((d) => (
                  <span key={d.slug} className="bg-[#e8f2fb] text-[#0d64a8] text-sm px-3 py-1.5 rounded-full">
                    <MapPin size={12} className="inline mr-1 -mt-0.5" /> {d.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {results.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Search className="text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">No packages match your search</h3>
              <p className="text-gray-500 mt-1">Try a different destination like “Goa”, “Sikkim” or “Bali”.</p>
              <Link to="/" className="inline-block mt-6 bg-[#0d64a8] text-white px-6 py-2.5 rounded-md text-sm">Explore all packages</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((p) => (
                <Link
                  to={`/package/${p.id}`}
                  key={p.id}
                  className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative">
                    <SafeImage src={p.image} alt={p.title} fallbackType={p.fbType || 'default'} className="w-full h-52 object-cover" />
                    {p.offer_enabled && (
                      <OfferRibbon text={p.offer_ribbon_text || 'Limited Offer'} endsAt={p.offer_ends_at} />
                    )}
                    {(() => {
                      const themeRaw = Array.isArray(p.themes) && p.themes.length > 0
                        ? p.themes[0]
                        : (p.category === 'india' ? 'India' : 'International');
                      const label = String(themeRaw).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                      return (
                        <div className="absolute top-3 right-3 bg-[#0d64a8] text-white text-[11px] font-medium px-2.5 py-1 rounded shadow-sm capitalize">
                          {label}
                        </div>
                      );
                    })()}
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white border border-gray-100 shadow px-4 py-2 rounded-md flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                      <Clock size={14} className="text-[#0d64a8]" /> {p.duration}
                    </div>
                  </div>
                  <div className="pt-8 px-5 pb-5">
                    <h3 className="text-[17px] font-semibold text-gray-900 mb-3 line-clamp-2 min-h-[48px]">{p.title}</h3>
                    <div className="flex items-start gap-1.5 text-sm text-gray-600 mb-4">
                      <MapPin size={14} className="text-[#0d64a8] mt-0.5 shrink-0" />
                      <span className="line-clamp-1">{p.itinerary}</span>
                    </div>
                    <div className="h-px bg-gray-100 my-3" />
                    <div className="flex items-end justify-between gap-2">
                      <PriceTag
                        price={p.price}
                        offerEnabled={p.offer_enabled}
                        offerPrice={p.offer_price}
                        size="md"
                        suffix="/ Adult"
                      />
                      <div className="flex items-center gap-1.5 shrink-0">
                        {p.offer_enabled && Number(p.offer_price) > 0 && Number(p.offer_price) < Number(p.price || 0) && (
                          <WhatsAppShareBtn pkg={p} variant="card" />
                        )}
                        <span className="bg-[#0d64a8] text-white text-xs font-medium px-3 py-2 rounded">View Details</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SearchResults;
